import { putJSON } from 'api/fetch';
import { isEmpty } from 'lodash';

import {
  einPublicKey,
  ssnPublicKey,
} from 'features/employeeOnboarding/constants';
import { CHECK_ADDRESS_ERROR } from 'features/team/drawers/TaxEntryDrawer/constants';

import { encryptWithPublicKey } from 'util/encryption';
import { toI18n } from 'util/i18n';

import { I18N_ERROR_PATH } from '../constants';

const checkAddressErrorMessage = (errorMessage, errorKey) => {
  if (errorMessage) {
    if (errorMessage === CHECK_ADDRESS_ERROR.messages.default) {
      return CHECK_ADDRESS_ERROR.messages.invalidAddress;
    }

    const message = errorMessage
      ?.split(CHECK_ADDRESS_ERROR.default_split_key)[1]
      ?.replace(
        CHECK_ADDRESS_ERROR.transformation_regex,
        CHECK_ADDRESS_ERROR.default_separator
      )
      ?.toLowerCase();

    if (errorKey) {
      return message === errorKey ? message : null;
    }

    return message;
  }
};

const anyContractorInfoFields = values =>
  values.contractor_info_business_name || values.ein;

const anyAddressFields = values =>
  values.address_1 ||
  values.address_2 ||
  values.city ||
  values.state ||
  values.zip_code;

export default (
  formData,
  currentCompanyId,
  values,
  actions,
  payrollAddressValidationRollout = false
) => {
  const payload = {
    legal_first_name: values.legal_first_name,
    middle_name: values.legal_middle_name,
    legal_last_name: values.legal_last_name,
    date_of_birth: values.date_of_birth,
    encrypted_ssn: encryptWithPublicKey(ssnPublicKey(), values.ssn) || null,
    address_attributes: anyAddressFields(values)
      ? {
          address_1: values.address_1,
          address_2: values.address_2,
          city: values.city,
          state: values.state,
          zip: isEmpty(values.zip_code) ? undefined : values.zip_code,
          current_company_id: currentCompanyId,
        }
      : undefined,
    is_payroll_info: true,
    contractor_info_attributes: anyContractorInfoFields(values)
      ? {
          id: formData.contractor_info_id,
          business_name: values.contractor_info_business_name,
          encrypted_ein:
            encryptWithPublicKey(einPublicKey(), values.ein.replace('-', '')) ||
            null,
        }
      : undefined,
  };

  actions.setFieldValue('ssn_error', undefined);

  return putJSON(`/employees/${formData.id}/update_personal_information.json`, {
    personal_information_params: payload,
  })
    .then(response => {
      if (response.address_errors && !isEmpty(response.address_errors)) {
        const addressErrors = response.address_errors.error;

        const errorMessage = checkAddressErrorMessage(addressErrors.message);

        if (
          errorMessage !== CHECK_ADDRESS_ERROR.messages.invalidAddress &&
          errorMessage !== CHECK_ADDRESS_ERROR.messages.unknownStreet
        ) {
          actions.setFieldValue(
            'address_suggestion',
            addressErrors.suggested_alternative,
            false
          );
        }

        actions.setFieldValue('address_suggestion_status', 'pending', false);

        const errors = {};

        const cityError = checkAddressErrorMessage(
          addressErrors.message,
          CHECK_ADDRESS_ERROR.messages.incorrectCity
        );
        if (cityError) {
          errors.city = toI18n(
            `${I18N_ERROR_PATH}.address_errors.${cityError}`
          );
        }

        const stateError = checkAddressErrorMessage(
          addressErrors.message,
          CHECK_ADDRESS_ERROR.messages.incorrectState
        );
        if (stateError) {
          errors.state = toI18n(
            `${I18N_ERROR_PATH}.address_errors.${stateError}`
          );
        }

        const zipError = checkAddressErrorMessage(
          addressErrors.message,
          CHECK_ADDRESS_ERROR.messages.incorrectPostalCode
        );
        if (zipError) {
          errors.zip_code = toI18n(
            `${I18N_ERROR_PATH}.address_errors.${zipError}`
          );
        }

        const addressError = checkAddressErrorMessage(
          addressErrors.message,
          CHECK_ADDRESS_ERROR.messages.unknownStreet
        );
        if (addressError) {
          errors.address_1 = toI18n(
            `${I18N_ERROR_PATH}.address_errors.${addressError}`
          );
        }
        actions.setErrors(errors);

        return Promise.reject();
      }
      return response;
    })
    .catch(error => {
      if (error) {
        error.response.json().then(err => {
          let errorMessage = toI18n('errors.generic');

          if (err?.error === 'ssn_validation_error') {
            errorMessage = toI18n(`${I18N_ERROR_PATH}.ssn_already_taken`, {
              props: { first_name: values.legal_first_name },
            });
          } else if (err?.message) {
            const errorValues = Object.values(err.message);
            const uniqueErrors = [...new Set(errorValues.flat())];
            errorMessage = uniqueErrors.join('. ');
          }

          actions.setFieldValue('ssn_error', errorMessage);
          if (payrollAddressValidationRollout) {
            if (err.address_check_error && !isEmpty(err.address_check_error)) {
              const addressErrors = err.address_check_error;

              const checkErrorMessage = checkAddressErrorMessage(
                addressErrors.message
              );

              if (
                checkErrorMessage !==
                  CHECK_ADDRESS_ERROR.messages.invalidAddress &&
                checkErrorMessage !== CHECK_ADDRESS_ERROR.messages.unknownStreet
              ) {
                actions.setFieldValue(
                  'address_suggestion',
                  addressErrors.suggested_alternative,
                  false
                );
              }

              actions.setFieldValue(
                'address_suggestion_status',
                'pending',
                false
              );

              const errors = {};

              const cityError = checkAddressErrorMessage(
                addressErrors.message,
                CHECK_ADDRESS_ERROR.messages.incorrectCity
              );
              if (cityError) {
                errors.city = toI18n(
                  `${I18N_ERROR_PATH}.address_errors.${cityError}`
                );
              }

              const stateError = checkAddressErrorMessage(
                addressErrors.message,
                CHECK_ADDRESS_ERROR.messages.incorrectState
              );
              if (stateError) {
                errors.state = toI18n(
                  `${I18N_ERROR_PATH}.address_errors.${stateError}`
                );
              }

              const zipError = checkAddressErrorMessage(
                addressErrors.message,
                CHECK_ADDRESS_ERROR.messages.incorrectPostalCode
              );
              if (zipError) {
                errors.zip_code = toI18n(
                  `${I18N_ERROR_PATH}.address_errors.${zipError}`
                );
              }

              const addressError = checkAddressErrorMessage(
                addressErrors.message,
                CHECK_ADDRESS_ERROR.messages.unknownStreet
              );
              if (addressError) {
                errors.address_1 = toI18n(
                  `${I18N_ERROR_PATH}.address_errors.${addressError}`
                );
              }
              actions.setErrors(errors);

              return Promise.reject();
            }
          }
        });
      }

      return Promise.reject(error);
    });
};
